/**
 * md-consent-gate.js — one place to ask "may we track this visitor?"
 *
 * WHY THIS FAILS OPEN, DELIBERATELY.
 * A consent gate that is subtly wrong does not fail loudly. It silently stops
 * trackers from firing, with no console error and no visible symptom, and the
 * first sign is someone noticing weeks later that conversions fell off a cliff.
 * So every helper here returns TRUE when it cannot get a real answer. The
 * failure mode is "no worse than today", never "tracking is dead and nobody
 * knows". Compliance value comes from the cases where the API IS available and
 * says no; it does not come from guessing when the API is missing.
 *
 * Consequence worth understanding: while Shopify's consent banner is disabled in
 * Admin, marketingAllowed() has no collected consent to read and this gate is
 * effectively inert. That is intentional. It lets the gating deploy first and
 * ACTIVATE later, when the banner is switched on, instead of both changing at
 * once during a deploy.
 *
 * Shopify's Customer Privacy API is the integration point rather than a
 * third-party CMP, because a second consent layer can double-fire signals and
 * conflict with Shopify's own privacy pixel system.
 */
(function () {
  'use strict';

  function cp() {
    try {
      return (window.Shopify && window.Shopify.customerPrivacy) || null;
    } catch (_) {
      return null;
    }
  }

  /* Global Privacy Control. Honouring it is required by twelve states as of
     2026 (CA, CO, CT, DE, MD, MN, MT, NJ, NH, OR, TX), not just California, and
     CA/CO/CT are running a joint enforcement sweep on exactly this failure. */
  function gpcOn() {
    try {
      return window.navigator && window.navigator.globalPrivacyControl === true;
    } catch (_) {
      return false;
    }
  }

  function allowed(method) {
    // GPC is an opt-out of sale/sharing. "Sharing" covers ordinary ad pixels —
    // a Meta or TikTok pixel sending a visitor id is sharing for cross-context
    // behavioural advertising even though we pay the vendor rather than being
    // paid. So an ad pixel must not fire under GPC regardless of what the
    // consent API reports.
    if (gpcOn() && (method === 'marketingAllowed' || method === 'saleOfDataAllowed')) {
      return false;
    }
    var api = cp();
    if (!api || typeof api[method] !== 'function') return true; // fail OPEN
    try {
      return api[method]() !== false;
    } catch (_) {
      return true; // fail OPEN
    }
  }

  var mdConsent = {
    /** Gate ad / marketing pixels (Meta, TikTok, Pinterest, Klaviyo marketing). */
    marketingAllowed: function () {
      return allowed('marketingAllowed');
    },
    /** Gate analytics (GA4, custom analytics events). */
    analyticsAllowed: function () {
      return allowed('analyticsProcessingAllowed');
    },
    /** CCPA/CPRA sale-or-share signal. */
    saleOfDataAllowed: function () {
      return allowed('saleOfDataAllowed');
    },
    gpc: gpcOn,
    /** True only when a real answer came from Shopify, not from the fail-open path. */
    isAuthoritative: function () {
      var api = cp();
      return !!(api && typeof api.marketingAllowed === 'function');
    },
  };

  window.mdConsent = mdConsent;

  /* ------------------------------------------------------------------------
     GOOGLE CONSENT MODE v2 -- the `update` half.

     layout/theme.liquid declares the `consent default` inline, before GTM
     loads: denied in the opt-in regions (EEA/UK/CH, CA-QC, US-CA), granted
     everywhere else. That default is static and cannot fail. This function
     supplies the other half -- the visitor's ACTUAL choice -- and is what
     turns the cookie banner from decorative into load-bearing.

     Measured on production 2026-09-01 before this shipped: with consent set to
     fully declined, google_tag_data.ics.entries was {} for all four signals and
     GA4 / Google Ads / Meta / TikTok / Pinterest / Bing every one still fired.

     Only pushed when Shopify actually answered (isAuthoritative). If the API is
     missing we deliberately push NOTHING and let the region default stand --
     pushing `granted` on a guess would defeat the EU/CA deny, and pushing
     `denied` on a guess would silently kill tracking worldwide. Staying quiet is
     the only option that is wrong in neither direction.
     ------------------------------------------------------------------------ */
  function pushConsentModeUpdate() {
    try {
      if (!mdConsent.isAuthoritative()) return;
      if (typeof window.gtag !== 'function') return;

      var marketing = mdConsent.marketingAllowed();
      var sale = mdConsent.saleOfDataAllowed();
      var analytics = mdConsent.analyticsAllowed();

      // ad_user_data / ad_personalization are the v2 additions: they cover
      // sending user data to Google and using it for personalised ads. Both are
      // "sharing" under CCPA, so a sale/share opt-out (including GPC) must deny
      // them even when marketing consent itself is present.
      var adsOk = marketing && sale ? 'granted' : 'denied';

      window.gtag('consent', 'update', {
        ad_storage: marketing ? 'granted' : 'denied',
        ad_user_data: adsOk,
        ad_personalization: adsOk,
        analytics_storage: analytics ? 'granted' : 'denied',
      });
    } catch (_) {
      /* a consent push must never break the page */
    }
  }

  mdConsent.syncConsentMode = pushConsentModeUpdate;

  /* ------------------------------------------------------------------------
     HUBSPOT -- the release half.

     layout/theme.liquid pushes _hsq doNotTrack inline, before
     content_for_header, because HubSpot is an app embed that Consent Mode and
     the GTM consent requirements cannot reach, and its first __ptq.gif beacon
     goes out before any deferred script could stop it. That default is closed.

     The queue is _hsq (tracking), NOT _hsp (privacy/banner). doNotTrack on _hsp
     is swallowed with no error and no effect -- measured on staging, where
     __ptq.gif still fired with a correctly ordered _hsp push in place. The ONE
     thing that does go on _hsp is setHubSpotConsent (cookie consent).

     This releases it once Shopify actually confirms analytics is permitted --
     which is every visitor outside the cookie-banner regions, so the steady
     state for most traffic is "tracking on". Kept in sync on later changes too:
     a visitor who accepts and then withdraws gets doNotTrack pushed back on.
     ------------------------------------------------------------------------ */
  function syncHubSpot() {
    try {
      if (!mdConsent.isAuthoritative()) return; // leave the closed default alone
      var internal = window.MD_INTERNAL_TRAFFIC === true;
      // HubSpot's COOKIE consent lives on _hsp (the privacy queue), separately
      // from doNotTrack on _hsq below. doNotTrack stops the __ptq beacon but
      // not the __hstc / hubspotutk cookies, which the 2026-10-09 probe saw set
      // after a Decline. This is the documented setHubSpotConsent command; it
      // only bites once the HubSpot cookie policy is set to opt-in (Admin).
      var hsp = (window._hsp = window._hsp || []);
      hsp.push([
        'setHubSpotConsent',
        {
          analytics: !internal && mdConsent.analyticsAllowed(),
          advertisement:
            !internal && mdConsent.marketingAllowed() && mdConsent.saleOfDataAllowed(),
          functionality: true,
        },
      ]);
      // Internal (staff) traffic keeps HubSpot analytics closed even with
      // consent. Chat, forms and the CRM are unaffected by doNotTrack.
      if (internal) return;
      var hsq = (window._hsq = window._hsq || []);
      if (mdConsent.analyticsAllowed()) {
        hsq.push(['doNotTrack', { track: true }]);
      } else {
        hsq.push(['doNotTrack']);
      }
    } catch (_) {
      /* never let a consent push break the page */
    }
  }

  mdConsent.syncHubSpot = syncHubSpot;

  /* ------------------------------------------------------------------------
     AD PIXELS ALREADY ON THE PAGE -- withdraw on a mid-page opt-out.

     GTM's consent requirements only decide whether a tag STARTS. A pixel that
     already loaded keeps sending after a Decline until the next page load:
     measured live 2026-10-21, TikTok sent pixel/inter about 4s after a Decline
     click on the same page. Each vendor's own consent call closes that gap:
     fbq('consent','revoke'), ttq.revokeConsent(), and the UET consent update.

     Revoke only on a real answer that says no (marketing or sale of data
     denied, which includes GPC). Grant only to undo a revoke made on this page,
     so a visitor who never opted out is never touched. Pinterest has no consent
     API; GTM's requirement is its only gate.
     ------------------------------------------------------------------------ */
  var adPixelsRevoked = false;

  function syncAdPixels() {
    var ok;
    try {
      if (!mdConsent.isAuthoritative()) return;
      ok = mdConsent.marketingAllowed() && mdConsent.saleOfDataAllowed();
    } catch (_) {
      return;
    }
    if (ok && !adPixelsRevoked) return;
    var cmd = ok ? 'grant' : 'revoke';
    // One try per vendor: a throw from one must not leave the others sending.
    try {
      if (typeof window.fbq === 'function') window.fbq('consent', cmd);
    } catch (_) {}
    try {
      var ttq = window.ttq;
      var fn = ttq && (ok ? ttq.grantConsent : ttq.revokeConsent);
      if (typeof fn === 'function') fn.call(ttq);
    } catch (_) {}
    try {
      // On a revoke, create the UET queue if the tag has not loaded yet, so the
      // denial is waiting for it. A grant only ever undoes our own revoke.
      if (!ok) window.uetq = window.uetq || [];
      if (window.uetq && typeof window.uetq.push === 'function') {
        window.uetq.push('consent', 'update', { ad_storage: ok ? 'granted' : 'denied' });
      }
    } catch (_) {}
    adPixelsRevoked = !ok;
  }

  mdConsent.syncAdPixels = syncAdPixels;

  /* ------------------------------------------------------------------------
     SESSION REPLAY (FigPii, Clarity) -- fails CLOSED, unlike everything above.

     A replay vendor records the visitor's whole session, and FigPii and Clarity
     both run outside the GTM consent requirements for at least part of their
     footprint (FigPii is a Shopify app embed; Clarity syncs Microsoft Ads ids
     through c.bing.com). Measured live 2026-10-09: both kept running after a
     banner Decline or a sale-of-data opt-out. See the footgun "A Banner
     'Decline' Does Not Stop App-Embed and Server-Side Vendors".

     Replay needs ALL of: a real answer from Shopify, analytics allowed, sale of
     data allowed, and no GPC. Missing API => no replay. The fail-open argument
     at the top of this file is about conversion pixels; losing some heatmaps
     when Shopify does not answer costs nothing a customer would notice.
     ------------------------------------------------------------------------ */

  /** True only when the visitor EXPLICITLY said yes to analytics. */
  function explicitAnalytics() {
    try {
      var api = cp();
      if (!api || typeof api.currentVisitorConsent !== 'function') return false;
      var c = api.currentVisitorConsent() || {};
      return c.analytics === 'yes';
    } catch (_) {
      return false;
    }
  }

  function replayAllowed() {
    try {
      return (
        mdConsent.isAuthoritative() &&
        mdConsent.analyticsAllowed() &&
        mdConsent.saleOfDataAllowed() &&
        !gpcOn()
      );
    } catch (_) {
      return false;
    }
  }

  mdConsent.explicitAnalytics = explicitAnalytics;
  mdConsent.replayAllowed = replayAllowed;

  /* FigPii reads the LAST ['setConsent', {value}] in window._fpEvent when its
     engine boots (read out of the shipped engine bundle), so queueing is safe
     before or after it loads. FIGPII.setConsent is the live call once it has. */
  function syncFigPii() {
    try {
      var value = replayAllowed();
      var q = (window._fpEvent = window._fpEvent || []);
      q.push(['setConsent', { value: value }]);
      try {
        if (window.FIGPII && typeof window.FIGPII.setConsent === 'function') {
          window.FIGPII.setConsent(value);
        }
      } catch (_) {}
    } catch (_) {
      /* never let a consent push break the page */
    }
  }

  /* Clarity loads from a GTM tag, usually AFTER this runs. Its own loader keeps
     an existing window.clarity queue (c[a] = c[a] || function () {...q.push}),
     so installing that same stub here queues the consent call for it instead of
     polling. md-internal-traffic.liquid uses the same stub for clarity('stop').

     consentv2 DENIED IS NOT A STOP: Clarity restarts in cookieless mode and
     keeps uploading to clarity.ms/collect (UAT 2026-10-10, S2 opt-out and S4
     decline). Only clarity('stop') halts collection, so when replay is not
     allowed we stop it, and clarity('start') resumes it. Neither is on the
     public API page; both are exported by clarity-js (src/clarity.ts) and
     dispatched by window.clarity(method), and the Clarity Flutter/web wrappers
     document them. Behaviour read from the clarity-js 0.8.72 source:
     - A stopped Clarity swaps window.clarity back to a queue stub, so every
       later call (ours, GTM's, the GCM listener's) just queues. Only 'start'
       restarts it, which makes stop a latch until we say otherwise.
     - Its tag loader moves its own 'start' to the FRONT of a pre-load queue,
       so a 'stop' queued before Clarity loads still runs after it starts.
     - consentv2 denied on a running, tracking Clarity does stop() then a timed
       start() itself; stopping FIRST means that restart never gets scheduled.
     - clarity('consent', false) is just consentv2 all-denied (APIv1), so it
       is not a stop either.
     Idempotent: stop/start fire only on a change, and a 'start' first drops
     any 'stop' WE queued, so it is not replayed after the restart. A 'stop'
     queued by md-internal-traffic.liquid is never touched. */
  var clarityStopped = false;
  var clarityQueuedStops = [];
  var clarityLastKey = null;
  var CLARITY_WATCH_MS = 500;
  var CLARITY_WATCH_MAX = 60; // ~30s for a late GTM tag
  var clarityWatchTimer = null;

  function clarityQueueStub() {
    if (typeof window.clarity !== 'function') {
      window.clarity = function () {
        (window.clarity.q = window.clarity.q || []).push(arguments);
      };
    }
  }

  function clarityStop() {
    window.clarity('stop');
    var q = window.clarity && window.clarity.q;
    if (q && q.length) clarityQueuedStops.push(q[q.length - 1]);
    clarityStopped = true;
  }

  function clarityStart() {
    var q = window.clarity && window.clarity.q;
    if (q && q.length && clarityQueuedStops.length) {
      for (var i = q.length - 1; i >= 0; i--) {
        if (clarityQueuedStops.indexOf(q[i]) !== -1) q.splice(i, 1);
      }
    }
    clarityQueuedStops = [];
    window.clarity('start');
    clarityStopped = false;
  }

  /* A live Clarity exposes window.clarity.v; a stopped or not-yet-loaded one is
     a queue stub without it. Seeing .v while replay is denied means Clarity is
     RECORDING (a late tag, or a re-fired one), so force the stop again. */
  function watchClarity() {
    if (clarityWatchTimer) return;
    var tries = 0;
    clarityWatchTimer = setInterval(function () {
      tries++;
      try {
        if (window.clarity && window.clarity.v && !replayAllowed())
          syncClarity(true);
      } catch (_) {}
      if (tries >= CLARITY_WATCH_MAX) {
        clearInterval(clarityWatchTimer);
        clarityWatchTimer = null;
      }
    }, CLARITY_WATCH_MS);
  }

  function syncClarity(force) {
    try {
      clarityQueueStub();
      var replay = replayAllowed();
      var ads = mdConsent.marketingAllowed() && mdConsent.saleOfDataAllowed();
      var key = (replay ? 'r' : '-') + (ads ? 'a' : '-');
      if (force !== true && key === clarityLastKey) return;
      clarityLastKey = key;
      if (!replay && (force === true || !clarityStopped)) clarityStop();
      else if (replay && clarityStopped) clarityStart();
      window.clarity('consentv2', {
        ad_Storage: ads ? 'granted' : 'denied',
        analytics_Storage: replay ? 'granted' : 'denied',
      });
      watchClarity();
    } catch (_) {
      /* never let a consent push break the page */
    }
  }

  mdConsent.syncFigPii = syncFigPii;
  mdConsent.syncClarity = syncClarity;

  /* md_cmirror: a first-party mirror of the last AUTHORITATIVE decision, so
     snippets/md-consent-early.liquid can act on a withdrawal at the very start
     of the next page, before FigPii's app embed loads. Shopify's own
     _tracking_consent cookie cannot be read from JS. A strictly necessary
     consent-record cookie: it holds the choice, never an identifier.
     Format: a=1&m=0&s=1&r=0 (analytics, marketing, sale of data, replay). */
  var MIRROR_COOKIE = 'md_cmirror';
  var MIRROR_MAX_AGE = 31536000; // one year

  function writeMirror() {
    try {
      if (!mdConsent.isAuthoritative()) return; // record answers, never guesses
      var bit = function (b) {
        return b ? '1' : '0';
      };
      var value =
        'a=' + bit(mdConsent.analyticsAllowed()) +
        '&m=' + bit(mdConsent.marketingAllowed()) +
        '&s=' + bit(mdConsent.saleOfDataAllowed()) +
        '&r=' + bit(replayAllowed());
      document.cookie =
        MIRROR_COOKIE + '=' + value + '; path=/; max-age=' + MIRROR_MAX_AGE + '; SameSite=Lax' +
        (window.location && window.location.protocol === 'https:' ? '; Secure' : '');
    } catch (_) {
      /* a consent record must never break the page */
    }
  }

  mdConsent.writeMirror = writeMirror;

  /** Every vendor sync, in one place: the loadFeatures callback and the
   *  visitorConsentCollected listener both call this. */
  function syncAll() {
    pushConsentModeUpdate();
    syncHubSpot();
    syncAdPixels();
    syncFigPii();
    syncClarity();
    writeMirror();
  }

  mdConsent.syncAll = syncAll;

  /* Record GPC as an explicit opt-out through Shopify's own API, so the signal
     is honoured by everything Shopify gates (Web Pixels Manager included) and
     not only by our own call sites. Best-effort: if the feature will not load,
     the per-call gpcOn() check above still protects our own pixels. */
  function recordGpcOptOut() {
    if (!gpcOn()) return;
    try {
      var api = cp();
      if (!api || typeof api.setTrackingConsent !== 'function') return;
      if (typeof api.currentVisitorConsent === 'function') {
        var c = api.currentVisitorConsent() || {};
        // Do not overwrite a choice the visitor actively made.
        if (c.marketing === 'no' || c.sale_of_data === 'no') return;
      }
      // Opt out of SALE/SHARING only. GPC is a sale-or-share opt-out under CCPA and
      // the other eleven states that honour it -- it does not require switching off
      // first-party analytics, and doing so needlessly discards GA4 data for every
      // privacy-conscious visitor. Verified in staging UAT: writing analytics:false
      // here made analyticsProcessingAllowed() return false downstream, which is
      // stricter than the statute asks and was not the intent.
      api.setTrackingConsent({ marketing: false, sale_of_data: false }, function () {});
    } catch (_) {
      /* never let a consent write break the page */
    }
  }

  /* STAFF BROWSERS (snippets/md-internal-traffic.liquid, reason
     'staff_cookie') hold Shopify consent all-denied, so every app pixel and
     embed that Shopify gates on consent stands down too. The snippet writes it
     at enrollment; this re-asserts it on any later view where it drifted (the
     visitor clicked Accept on the banner, or the consent cookie expired).
     Written ONLY when some purpose is not already 'no', so a steady-state
     staff view makes no consent request at all.
     NEVER for 'render_job': that is a per-page automation flag, and a consent
     write would persist past the page. Nothing here ever writes all-true. */
  var STAFF_DENIED = {
    analytics: false,
    marketing: false,
    preferences: false,
    sale_of_data: false,
  };
  var PURPOSES = ['analytics', 'marketing', 'preferences', 'sale_of_data'];

  function isStaffCookie() {
    return (
      window.MD_INTERNAL_TRAFFIC === true &&
      window.MD_INTERNAL_TRAFFIC_REASON === 'staff_cookie'
    );
  }

  function assertStaffDenied() {
    try {
      var api = cp();
      if (!api || typeof api.setTrackingConsent !== 'function') return;
      if (typeof api.currentVisitorConsent === 'function') {
        var c = api.currentVisitorConsent() || {};
        var allNo = PURPOSES.every(function (k) {
          return c[k] === 'no';
        });
        if (allNo) return;
      }
      api.setTrackingConsent(STAFF_DENIED, function () {});
    } catch (_) {
      /* never let a consent write break the page */
    }
  }

  mdConsent.assertStaffDenied = assertStaffDenied;

  /* After ?md_staff=0 (snippet sets sessionStorage md_staff_restore), open
     Shopify's consent preferences once, so the person can replace the
     all-denied choice staff mode left behind. The flag is cleared on the first
     attempt either way: one prompt, never a nag. */
  function restoreAfterStaff() {
    var flagged = false;
    try {
      flagged = window.sessionStorage.getItem('md_staff_restore') === '1';
      if (flagged) window.sessionStorage.removeItem('md_staff_restore');
    } catch (_) {
      return;
    }
    if (!flagged || window.MD_INTERNAL_TRAFFIC === true) return;
    var tries = 0;
    (function attempt() {
      try {
        var banner = window.privacyBanner;
        if (banner && typeof banner.showPreferences === 'function') {
          banner.showPreferences();
          return;
        }
      } catch (_) {
        return;
      }
      // The banner script may load after the consent API; wait up to ~5s.
      if (++tries < 20) setTimeout(attempt, 250);
    })();
  }

  mdConsent.restoreAfterStaff = restoreAfterStaff;

  /* Session replay fails CLOSED when Shopify never answers (no loadFeatures,
     or it errors): replayAllowed() is false there, so this queues
     setConsent false for FigPii and denied for Clarity. Without it, an outage
     left FigPii with no setConsent at all. Nothing else is synced on these
     paths: the conversion pixels deliberately fail open (see top of file). */
  function holdReplay() {
    syncFigPii();
    syncClarity();
  }

  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (err) {
          if (err) holdReplay();
          if (!err) {
            if (isStaffCookie()) {
              assertStaffDenied();
            } else if (window.MD_INTERNAL_TRAFFIC !== true) {
              // Customers only: a staff browser is already all-denied, and a
              // render job must never persist a consent write.
              recordGpcOptOut();
            }
            // Either write may have changed consent, so read the state AFTER
            // it, not before.
            syncAll();
          }
          restoreAfterStaff();
        }
      );
    } else {
      holdReplay();
    }
  } catch (_) {
    holdReplay();
  }

  /* Re-evaluate when the visitor makes a choice, so late-loading call sites and
     anything listening can react. trackingConsentAccepted is an EVENT name, not
     a method — calling it as a method throws TypeError. */
  try {
    document.addEventListener('visitorConsentCollected', function (e) {
      try {
        syncAll();
        document.dispatchEvent(
          new CustomEvent('md:consent-changed', { detail: (e && e.detail) || {} })
        );
      } catch (_) {}
    });
  } catch (_) {}

  /* GPC confirmation. CCPA regs section 7025(c)(6) (effective 2026-01-01)
     require the site to DISPLAY that a Global Privacy Control signal was
     honoured, e.g. "Opt-Out Request Honored". recordGpcOptOut() above does the
     honouring; this makes it visible next to the footer privacy-choices link.
     Text only, no role/live region: it is static page content, present from
     the moment the footer renders, not a status change. */
  function showGpcHonored() {
    if (!gpcOn()) return;
    try {
      var links = document.querySelectorAll('[data-md-privacy-choices]');
      for (var i = 0; i < links.length; i++) {
        var link = links[i];
        var next = link.nextElementSibling;
        if (next && next.hasAttribute('data-md-gpc-honored')) continue;
        var note = document.createElement('span');
        note.setAttribute('data-md-gpc-honored', '');
        note.textContent = ' (Opt-Out Request Honored)';
        link.insertAdjacentElement('afterend', note);
      }
    } catch (_) {
      /* a notice must never break the page */
    }
  }

  mdConsent.showGpcHonored = showGpcHonored;

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', showGpcHonored);
    } else {
      showGpcHonored();
    }
  } catch (_) {}

  /* "Do Not Sell or Share My Personal Information" — make the link do something.

     The footer link (sections/footer.liquid, [data-md-privacy-choices]) carried
     that label while pointing at /policies/privacy-policy, where the same link
     appears again pointing at itself. Measured 2026-09-08: there was no opt-out
     control anywhere in that loop. A CCPA/CPRA link that reaches no mechanism is
     the thing enforcement actions are actually built on, so this wires it to the
     real control.

     window.privacyBanner.showPreferences() was verified present on production
     before being used here, not assumed: the object exposes exactly
     [boot, loadBanner, showBanner, showPreferences], and a probe for a
     non-existent `hideBanner` correctly returned undefined. That negative result
     is the point — it proves this is a real object rather than something that
     answers to any property name, which is the trap `window.klaviyo` sets.

     Progressive enhancement on purpose: the href stays, so no-JS visitors and
     any future failure of the banner API still reach the policy rather than a
     dead anchor. We only preventDefault once showPreferences has actually been
     called without throwing. */
  /* The "Cookie preferences" footer link ([data-md-cookie-preferences]) opens the
     same dialog. Shopify injects privacyBanner itself and documents no way to load
     it on demand (loadFeatures only yields customerPrivacy), so a click that lands
     before it arrives waits briefly for it instead of leaving straight for the
     policy page. A modified or middle click keeps the browser's own behaviour. */
  var PREFS_SELECTOR = '[data-md-privacy-choices], [data-md-cookie-preferences]';
  var PREFS_WAIT_MS = 1500;
  var PREFS_POLL_MS = 100;
  var prefsPending = false;

  function preferencesApi() {
    var banner = window.privacyBanner;
    return banner && typeof banner.showPreferences === 'function' ? banner : null;
  }

  /** Opens the dialog; false if the API threw (the caller then navigates). */
  function openPreferences(banner) {
    try {
      var result = banner.showPreferences();
      if (result && typeof result.catch === 'function') result.catch(function () {});
      return true;
    } catch (_) {
      return false;
    }
  }

  mdConsent._navigate = function (href) {
    window.location.assign(href);
  };

  try {
    document.addEventListener('click', function (e) {
      // Only the live instance acts: a second evaluation of this file (Theme
      // Editor reload) must not open the dialog or navigate twice.
      if (window.mdConsent !== mdConsent) return;
      var link;
      try {
        link = e.target && e.target.closest && e.target.closest(PREFS_SELECTOR);
      } catch (_) {
        return;
      }
      if (!link) return;
      // A modified or middle click asks for the policy page in a new tab: honour it.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      var banner = preferencesApi();
      if (banner) {
        // A throw leaves the navigation alone: the policy page is the fallback.
        if (openPreferences(banner)) e.preventDefault();
        return;
      }
      var href = link.getAttribute('href');
      if (!href) return;
      e.preventDefault();
      if (prefsPending) return;
      prefsPending = true;
      var waited = 0;
      (function poll() {
        var late = preferencesApi();
        if (late || waited >= PREFS_WAIT_MS) {
          prefsPending = false;
          if (!(late && openPreferences(late))) mdConsent._navigate(href);
          return;
        }
        waited += PREFS_POLL_MS;
        setTimeout(poll, PREFS_POLL_MS);
      })();
    });
  } catch (_) {}
})();
